<template>
  <div class="w-full h-full space-y-4">
    <section>
      <div
        class="grid grid-cols-1 gap-4 *:data-[slot=card]:shadow-xs @xl/main:grid-cols-2 @5xl/main:grid-cols-4 md:grid-cols-4 dark:*:data-[slot=card]:bg-card"
      >
        <Card v-for="card in statCards" :key="card.title" class="@container/card">
          <CardHeader>
            <CardDescription>{{ card.title }}</CardDescription>
            <CardTitle class="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
              <Skeleton v-if="!programOfferStatistics" class="h-8 w-16" />
              <span v-else>{{ card.value }}</span>
            </CardTitle>
            <CardAction>
              <component :is="card.icon" class="size-5 text-card-icon" />
            </CardAction>
          </CardHeader>
        </Card>
      </div>
    </section>

    <!-- Cardlytics: personalised card-linked offers. Status and activation come from
         GET /publisher (cardlyticsStatus / cardlyticsActivated); the state was already
         fetched here but had no UI, so the publisher could not see or act on it. -->
    <Card>
      <CardHeader>
        <CardTitle>Personalised end user offers</CardTitle>
        <CardDescription>
          Activate personalised card-linked cashback offers powered by Cardlytics
        </CardDescription>
        <CardAction>
          <!-- Not started: the only actionable state. -->
          <Button
            v-if="cardlyticsStatus === 'NOT_STARTED'"
            size="sm"
            @click="activateCardlytics()"
          >
            <Play class="w-4 h-4" />Activate
          </Button>

          <!-- Live: show whether it is running and let the publisher pause/resume. -->
          <div v-else-if="cardlyticsStatus === 'AVAILABLE'" class="flex items-center gap-2">
            <Badge :variant="cardlyticsActive ? 'success' : 'secondary'">
              {{ cardlyticsActive ? 'Live' : 'Paused' }}
            </Badge>
            <Button size="sm" variant="outline" @click="toggleCardlytics()">
              <component :is="cardlyticsActive ? Pause : Play" class="w-4 h-4" />
              {{ cardlyticsActive ? 'Pause' : 'Resume' }}
            </Button>
          </div>

          <!-- Everything in between is Boogi-side onboarding: informational only. -->
          <Badge v-else variant="secondary">{{ cardlyticsStatusLabel }}</Badge>
        </CardAction>
      </CardHeader>
    </Card>

    <Card v-if="!showDeactivatedOffers">
      <CardHeader>
        <CardTitle>Your program</CardTitle>
        <CardDescription>Manage approved offers on your rewards program</CardDescription>
        <CardAction>
          <Button variant="outline" size="sm" id="createContract" @click="$router.push('/offer-catalogue')">
            Offers catalogue
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent class="space-y-4">
        <DataTable :headers="headersTable1" :items="retailers" :loading="loading"
          v-model:server-options="serverOptions" :server-items-length="serverItemsLength"
          searchable
          search-placeholder="Search by name, offer ID or retailer offer ID"
          :search="retailerSearchInput"
          @update:search="retailerSearchInput = $event"
          @search="getRetailerBySearch(0, serverOptions.rowsPerPage, retailerSearchInput)"
          @clear-search="onClearSearch()"
          :filter-groups="offerFilterGroups"
          :filters="offerFilters"
          @filter-change="onOfferFilterChange"
          :error="error"
          @retry="load(serverOptions.page - 1, serverOptions.rowsPerPage)">
          <template #toolbar-actions>
            <Tabs v-model="activeView" @update:model-value="onViewChange">
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="country">By country</TabsTrigger>
                <TabsTrigger value="segment">By segment</TabsTrigger>
              </TabsList>
            </Tabs>
            <Select v-model="orderBy" @update:model-value="onSortChange">
              <SelectTrigger size="sm" class="w-40" aria-label="Order by">
                <SelectValue placeholder="Order by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="o in orderByList" :key="o.id" :value="o.id">{{ o.text }}</SelectItem>
              </SelectContent>
            </Select>
          </template>

          <!-- Country / segment strips sit under the search bar, not above it. -->
          <template #toolbar-below>
            <Tabs v-if="activeView === 'country'" v-model="activeCountry" @update:model-value="onCountryChange">
              <TabsList class="flex-wrap">
                <TabsTrigger v-for="c in countries" :key="c" :value="c">{{ c }}</TabsTrigger>
              </TabsList>
              <p v-if="!countries.length" class="text-sm text-muted-foreground py-2">
                No countries found in your program yet.
              </p>
            </Tabs>

            <Tabs v-if="activeView === 'segment'" v-model="activeSegment" @update:model-value="onSegmentChange">
              <TabsList class="flex-wrap">
                <TabsTrigger v-for="s in customerSegments" :key="s.id" :value="s.id">{{ s.text }}</TabsTrigger>
              </TabsList>
              <p v-if="!customerSegments.length" class="text-sm text-muted-foreground py-2">
                No user segments created yet.
              </p>
            </Tabs>
          </template>

          <template #item-retailer="retailers">
            <div class="flex items-center gap-2.5">
              <img class="w-8 h-8 rounded-md border border-border object-contain shrink-0 bg-white"
                :src="retailers.offer.displayInformation.logo" />
              <div class="space-y-0.5 min-w-0">
                <p class="text-sm font-medium leading-tight truncate">{{ retailers.retailer.retailerName }}</p>
                <CopyId :value="retailers.id" :max-width="120" />
              </div>
            </div>
          </template>

          <!-- Country then type, side by side rather than stacked. -->
          <template #item-offertype="retailers">
            <div class="flex flex-wrap gap-1 items-center">
              <Badge v-if="retailers.offer.country" variant="secondary">{{ retailers.offer.country }}</Badge>
              <Badge variant="outline">{{ formatOfferType(retailers.offer.offerType) }}</Badge>
            </div>
          </template>
          <template #item-cashbackAmount="retailers">
            <div class="space-y-1">
              <p class="text-sm font-semibold">{{ cashbackHeadline(retailers) }}</p>
              <p class="text-xs text-muted-foreground">{{ cashbackCap(retailers) }}</p>
            </div>
          </template>

          <template #item-active="active">
            <span class="flex items-center gap-1.5 text-sm text-muted-foreground whitespace-nowrap">
              <component :is="active.active ? CircleCheck : CircleSlash"
                :class="['size-3.5', active.active ? 'text-green-500 dark:text-green-400' : 'text-muted-foreground']" />
              {{ active.active ? 'Live' : 'Deactivated' }}
            </span>
          </template>
          <template #item-action="retailers">
            <div class="flex justify-end items-center gap-1">
              <Button variant="outline" size="sm" @click="$router.push(`/retailer/${retailers.retailer.id}/offer/${retailers.offer.id}`)">View</Button>
            </div>
          </template>

          <template #item-date="retailers">
            <div class="flex justify-end items-center gap-1 text-sm text-muted-foreground whitespace-nowrap">
              {{ niceDateTime(retailers.createdOn) }}
            </div>
          </template>
        </DataTable>
      </CardContent>
    </Card>
  </div>
</template>

<script setup></script>

<script>
import { DateTime } from 'luxon'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardAction,
  CardFooter,
  Skeleton,
  Button,
  Badge,
  Input,
  Checkbox,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
  CopyId,
  Tabs,
  TabsList,
  TabsTrigger,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@boogi/ui'
import { CreditCard, MousePointer2, Link2, Filter, X, Play, Pause, CircleCheck, CircleSlash, Plus } from 'lucide-vue-next'
import { DataTable } from '@boogi/ui'

export default {
  name: 'manage-publsihers',
  components: {
    DataTable,
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardAction,
    CardFooter,
    Skeleton,
    Button,
    Badge,
    Input,
    Checkbox,
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogAction,
    AlertDialogCancel,
    CopyId,
    Tabs,
    TabsList,
    TabsTrigger,
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
    CreditCard,
    MousePointer2,
    Filter,
    X,
    Play,
    Pause,
    Plus,
  },
  props: {},

  data() {
    return {
      programOfferStatistics: undefined,
      displayCardlyticsOffers: true,
      cardlyticsStatus: 'SETTING_UP_ACCOUNT',
      cardlyticsActive: true,
      Play,
      Pause,
      showFilters: false,
      showDeactivatedOffers: false,
      serverOptions: {
        page: 1,
        rowsPerPage: 25,
      },
      successAction: '',
      serverItemsLength: undefined,
      showSuccessModal: false,
      loading: true,
      error: false,
      headersTable1: [
        { text: 'Offer', value: 'retailer' },
        { text: 'Details', value: 'offertype' },
        { text: 'Cashback', value: 'cashbackAmount' },
        { text: 'Status', value: 'active' },
        { text: 'Action', value: 'action' },
      ],
      activeView: 'all',
      activeCountry: '',
      activeSegment: '',
      countries: [],
      retailers: [],
      serverItemsLengthDeactive: undefined,
      serverOptions2: {
        page: 1,
        rowsPerPage: 25,
      },
      deactivated: [],
      orderByList: [
        { id: 'MOSTPOPULAR', text: 'Most Popular' },
        { id: 'ATOZ', text: 'A to Z' },
        { id: 'ZTOA', text: 'Z to A' },
      ],
      offerTypes: [
        { id: 'PAYMENT', text: 'Payment-linked offers' },
        { id: 'ONLINE', text: 'Affiliate links' },
        { id: 'CLICK_TO_ACTIVATE', text: 'Click to activate' },
      ],
      statuses: [
        { id: true, text: 'Live' },
        { id: false, text: 'Deactivated' },
      ],

      customerSegments: [],

      activeFilters: [
        { id: 'PAYMENT', filter: true },
        { id: 'ONLINE', filter: true },
        { id: 'GIFTCARD', filter: true },
        { id: 'CLICK_TO_ACTIVATE', text: 'Click to activate' },
        { id: 'Category', filterText: 'Restaurant' },
      ],
      offerType: [],
      selectStatus: [],
      orderBy: 'MOSTPOPULAR',
      retailerSearchInput: '',
      CircleCheck,
      CircleSlash,
    }
  },
  created() {
    this.getPublisher()
    this.getProgramOfferStatistics()
    this.getProgramOffers(0, 25)
    this.getCustomerTiers()
    this.getProgramCountries()
  },
  methods: {
    formatOfferType(t) {
      return ({ CARD_LINKED: 'Card-linked', AFFILIATE: 'Affiliate', IN_STORE: 'In-store', ONLINE: 'Online', PAYMENT: 'Payment-linked', CLICK_TO_ACTIVATE: 'Click to activate' })[t] || t
    },

    // The cashback value of the offer's headline rule (guarded — some offers arrive
    // without a fully-populated rule).
    cashbackValue(row) {
      return (
        (row &&
          row.cashbackEarnings &&
          row.cashbackEarnings[0] &&
          row.cashbackEarnings[0].conditions &&
          row.cashbackEarnings[0].conditions[0] &&
          row.cashbackEarnings[0].conditions[0].value) ||
        {}
      )
    },

    // Headline rate: a percentage for PERCENTAGE / PERCENTAGE_MAX, a £ amount for FIXED.
    // The old inline check only matched exact 'PERCENTAGE', so a "2% up to £2" offer
    // (PERCENTAGE_MAX) fell through and rendered its rate as "£2".
    cashbackHeadline(row) {
      const amount =
        row.displayRules && row.displayRules[0] && row.displayRules[0].highestCashbackPercentageAmount
      if (amount == null) return '—'
      const type = this.cashbackValue(row).type
      const isPercentage = type === 'PERCENTAGE' || type === 'PERCENTAGE_MAX'
      return `${isPercentage ? '' : '£'}${amount}${isPercentage ? '%' : ''}`
    },

    // Cap label. A PERCENTAGE_MAX offer caps each transaction's cashback at value.maximum
    // ("2% up to £2 per transaction"); other offers may carry a per-user spending cap.
    cashbackCap(row) {
      const value = this.cashbackValue(row)
      if (value.type === 'PERCENTAGE_MAX' && value.maximum > 0) {
        return `Up to £${value.maximum} per transaction`
      }
      if (row.offer && (row.offer.spendingCapped || row.offer.spendingCapAmount > 0)) {
        return `Capped at £${row.offer.spendingCapAmount} per user`
      }
      return 'Uncapped'
    },
    getCurrencySymbol(currencyCode, locale = 'en-US') {
      return (0)
        .toLocaleString(locale, {
          style: 'currency',
          currency: currencyCode,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
        .replace(/\d/g, '')
        .trim() // strip digits, leave symbol
    },

    selectedSegments(retailer) {
      if (retailer.customerTierType == 'TIERED') {
        const uniqueTexts = Array.from(
          new Set(
            retailer.selectedCustomerTiers
              .map((id) => {
                const match = this.customerSegments.find((seg) => seg.id === id)
                return match ? match.text : null
              })
              .filter(Boolean)
          )
        )

        return uniqueTexts
      } else {
        return []
      }
    },

    async getPublisher() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        const response = await this.$apiFetch(`${apiUrl}/private/api/client/publisher`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        })

        if (response.ok) {
          var object = await response.json()

          this.cardlyticsStatus = object.cardlyticsStatus
          this.cardlyticsActive = object.cardlyticsActivated
        }
      } catch {
        return
      }
    },

    async activateCardlytics() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/publisher/activate-cardlytics`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()

          this.cardlyticsStatus = object.cardlyticsStatus
          this.cardlyticsActive = object.cardlyticsActivated
        }
      } catch {
        return
      }
    },

    async toggleCardlytics() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/publisher/change-cardlytics`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()

          this.cardlyticsStatus = object.cardlyticsStatus
          this.cardlyticsActive = object.cardlyticsActivated
        }
      } catch {
        return
      }
    },

    async getProgramOfferStatistics() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/activated-offer/statistics`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.programOfferStatistics = object
        }
      } catch {
        return
      }
    },

    async getCustomerTiers() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loading = true

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/customer-tier/for-offers`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.customerSegments = object.map((tier) => ({
            id: tier.id,
            text: tier.name,
          }))

          this.loading = false
        }
      } catch (error) {
        return
      }
    },

    niceDateTime(dateObject) {
      return DateTime.fromMillis(dateObject).toLocaleString(DateTime.DATETIME_MED)
    },

    onClearSearch() {
      this.retailerSearchInput = ''
      this.resetToFirstPage()
      this.load(0, this.serverOptions.rowsPerPage)
    },

    onOfferFilterChange(updated) {
      const statusIds = (updated && updated.status) || []
      const typeIds = (updated && updated.offerType) || []
      this.selectStatus = this.statuses.filter((s) => statusIds.includes(s.id))
      this.offerType = this.offerTypes.filter((o) => typeIds.includes(o.id))
      this.resetToFirstPage()
      this.load(0, this.serverOptions.rowsPerPage)
    },

    async getRetailerBySearch(page, size, retailerSearch) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      if (this.offerType.length > 0) {
        var offerType = []
        this.offerType.forEach((element) => {
          offerType.push(element.id)
        })
      }

      if (this.selectStatus.length > 0) {
        var selectStatus = []
        this.selectStatus.forEach((element) => {
          selectStatus.push(element.id)
        })
      }

      this.loading = true
      this.error = false

      const countryParam = this.activeView === 'country' && this.activeCountry ? this.activeCountry : ''

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/activated-offer/retailer-search?page=${page}&search=${retailerSearch}&size=${size}&type=${this.offerType.length > 0 ? offerType : ''}&sort=${this.orderBy}&status=${this.selectStatus.length > 0 ? selectStatus : ''}${countryParam ? `&country=${countryParam}` : ''}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.retailers = object.content
          this.serverItemsLength = object.totalElements
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    async activateOffer(offerId) {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/activated-offer/offer/activate/${offerId}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          this.successAction = 'Offer Activated'
          this.getProgramOffers(0, 25)
          this.showSuccessModal = true
        }
      } catch {
        return
      }
    },

    async deActivateOffer(offerId) {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/activated-offer/offer/deactivate/${offerId}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          this.getProgramOffers(0, 25)
          this.successAction = 'Offer deactivated'
          this.showSuccessModal = true
        }
      } catch {
        return
      }
    },

    async getProgramOffers(page, size) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loading = true
      this.error = false

      if (this.offerType.length > 0) {
        var offerType = []
        this.offerType.forEach((element) => {
          offerType.push(element.id)
        })
      }

      if (this.selectStatus.length > 0) {
        var selectStatus = []
        this.selectStatus.forEach((element) => {
          selectStatus.push(element.id)
        })
      }

      const countryParam = this.activeView === 'country' && this.activeCountry ? this.activeCountry : ''

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/activated-offer?page=${page}&size=${size}&type=${this.offerType.length > 0 ? offerType : ''}&sort=${this.orderBy}&status=${this.selectStatus.length > 0 ? selectStatus : ''}${countryParam ? `&country=${countryParam}` : ''}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()

          this.serverItemsLength = object.totalElements
          this.retailers = object.content
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    async getOffersForCustomerTier(page, size) {
      if (!this.activeSegment) {
        this.retailers = []
        this.serverItemsLength = 0
        return
      }
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loading = true
      this.error = false

      const offerType = this.offerType.map((o) => o.id)
      const selectStatus = this.selectStatus.map((s) => s.id)

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/activated-offer/customer-tier/${this.activeSegment}?page=${page}&size=${size}&type=${offerType.length > 0 ? offerType : ''}&sort=${this.orderBy}&status=${selectStatus.length > 0 ? selectStatus : ''}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.serverItemsLength = object.totalElements
          this.retailers = object.content
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    async getProgramCountries() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/activated-offer/countries`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )
        if (response.ok) {
          this.countries = await response.json()
        }
      } catch (e) {
        return
      }
    },

    // Single entry point that dispatches to the fetch for the active view/tab.
    load(page, size) {
      if (this.retailerSearchInput && this.activeView !== 'segment') {
        return this.getRetailerBySearch(page, size, this.retailerSearchInput)
      }
      if (this.activeView === 'segment') {
        return this.getOffersForCustomerTier(page, size)
      }
      return this.getProgramOffers(page, size)
    },

    resetToFirstPage() {
      this.serverOptions = { page: 1, rowsPerPage: this.serverOptions.rowsPerPage || 25 }
    },

    onSortChange() {
      this.resetToFirstPage()
      this.load(0, this.serverOptions.rowsPerPage)
    },

    onViewChange(view) {
      this.activeView = view
      this.retailerSearchInput = ''
      if (view === 'country' && !this.activeCountry && this.countries.length) {
        this.activeCountry = this.countries[0]
      }
      if (view === 'segment' && !this.activeSegment && this.customerSegments.length) {
        this.activeSegment = this.customerSegments[0].id
      }
      this.resetToFirstPage()
      this.load(0, this.serverOptions.rowsPerPage)
    },

    onCountryChange(country) {
      this.activeCountry = country
      this.resetToFirstPage()
      this.load(0, this.serverOptions.rowsPerPage)
    },

    onSegmentChange(segmentId) {
      this.activeSegment = segmentId
      this.resetToFirstPage()
      this.load(0, this.serverOptions.rowsPerPage)
    },
  },
  computed: {
    generateCardlyticsStatus() {
      return String(this.cardlyticsStatus || '').toLocaleLowerCase()
    },

    // The intermediate states are all Boogi-side onboarding steps, so they read as one
    // "Setup in progress" to the publisher rather than leaking the raw enum.
    cardlyticsStatusLabel() {
      return (
        {
          NOT_STARTED: 'Not started',
          SETTING_UP_ACCOUNT: 'Setup in progress',
          HISTORICAL_DATA_UPLOAD: 'Setup in progress',
          SETTING_UP_TARGETING: 'Setup in progress',
          AVAILABLE: 'Available',
        }[this.cardlyticsStatus] || 'Setup in progress'
      )
    },
    statCards() {
      const s = this.programOfferStatistics || {}
      return [
        {
          title: 'Active offers',
          value: Number(s.totalActiveOffers || 0).toLocaleString(),
          icon: CircleCheck,
          footerTitle: 'Active offers',
          footerNote: 'Available across your program',
        },
        {
          title: 'Payment-linked',
          value: Number(s.totalPaymentLinkedOffersApproved || 0).toLocaleString(),
          icon: CreditCard,
          footerTitle: 'Card-linked offers',
          footerNote: 'Approved and live',
        },
        {
          title: 'Affiliate',
          value: Number(s.totalOnlineOffersApproved || 0).toLocaleString(),
          icon: Link2,
          footerTitle: 'Affiliate links',
          footerNote: 'Approved and live',
        },
        {
          title: 'Click-to-activate',
          value: Number(s.totalClickToActivateApproved || 0).toLocaleString(),
          icon: MousePointer2,
          footerTitle: 'Click-to-activate',
          footerNote: 'Approved and live',
        },
      ]
    },
    offerFilterGroups() {
      return [
        {
          key: 'status',
          label: 'Offer status',
          options: this.statuses.map((s) => ({ value: s.id, label: s.text })),
        },
        {
          key: 'offerType',
          label: 'Offer types',
          options: this.offerTypes.map((o) => ({ value: o.id, label: o.text })),
        },
      ]
    },
    offerFilters() {
      return {
        status: this.selectStatus.map((s) => s.id),
        offerType: this.offerType.map((o) => o.id),
      }
    },
  },
  mounted() {
    document.title = 'Dashboard | Program offers'
  },
  watch: {
    serverOptions: {
      deep: true,
      handler(val) {
        this.load(val.page - 1, val.rowsPerPage)
      },
    },
  },
}
</script>
